/**
 * Comment Rating System for PocketOption Blog
 *
 * This script handles the upvote and downvote functionality for comments.
 * It interacts with the WordPress REST API to record votes and update UI.
 * Uses vanilla JS with no dependencies on jQuery.
 */

// Selectors for DOM elements
const selectors = {
  upvoteButton: '.po-comment__feedback-plus',
  downvoteButton: '.po-comment__feedback-minus',
  ratingValue: '.po-comment__feedback-value',
  commentBody: '.po-comment__body',
  comment: '.po-comment'
};

// CSS classes used for styling
const cssClasses = {
  voted: 'po-comment__feedback-action--voted',
  positive: 'po-comment__feedback-value_positive',
  negative: 'po-comment__feedback-value_negative',
  muted: 'po-comment__body_muted'
};

// Configuration (use window global if available or fallback to defaults)
const config = window.poCommentRating || {
  ajaxUrl: `${pocket.siteurl}/wp-json/authbridge/v1/comment-vote`,
  isLoggedIn: false,
  loginPrompt: 'Please log in to vote on comments.',
  nonce: ''
};

/**
 * Handle upvote button click
 * @param {Event} e The click event
 */
const handleUpvote = (e) => {
  e.preventDefault();
  const button = e.target.closest(selectors.upvoteButton);
  handleVote(button, 1);
};

/**
 * Handle downvote button click
 * @param {Event} e The click event
 */
const handleDownvote = (e) => {
  e.preventDefault();
  const button = e.target.closest(selectors.downvoteButton);
  handleVote(button, -1);
};

/**
 * Process a vote action
 * @param {HTMLElement} element The clicked button
 * @param {number} voteType The vote type (1 for upvote, -1 for downvote)
 */
const handleVote = (element, voteType) => {
  // Check if user is logged in
  if (!config.isLoggedIn) {
    alert(config.loginPrompt);
    return;
  }
  
  const commentId = element.dataset.commentId;
  const comment = element.closest(selectors.comment);
  const ratingValue = comment.querySelector(selectors.ratingValue);
  const upvoteButton = comment.querySelector(selectors.upvoteButton);
  const downvoteButton = comment.querySelector(selectors.downvoteButton);
  const commentBody = comment.querySelector(selectors.commentBody);
  
  // Disable buttons while processing
  upvoteButton.disabled = true;
  downvoteButton.disabled = true;
  
  // Make the API request using fetch
  fetch(config.ajaxUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-WP-Nonce': config.nonce
    },
    body: JSON.stringify({
      comment_id: commentId,
      vote_type: voteType
    })
  })
  .then(response => {
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    return response.json();
  })
  .then(response => {
    // Update the UI
    if (response.success) {
      // Update the rating value
      ratingValue.textContent = response.total_rating;
      
      // Update CSS class based on the rating value
      if (response.total_rating >= 0) {
        ratingValue.classList.remove(cssClasses.negative);
        ratingValue.classList.add(cssClasses.positive);
      } else {
        ratingValue.classList.remove(cssClasses.positive);
        ratingValue.classList.add(cssClasses.negative);
      }
      
      // Apply muted class if rating is below threshold
      if (response.total_rating < -5) {
        commentBody.classList.add(cssClasses.muted);
      } else {
        commentBody.classList.remove(cssClasses.muted);
      }
      
      // Update button styling
      upvoteButton.classList.remove(cssClasses.voted);
      downvoteButton.classList.remove(cssClasses.voted);
      
      if (!response.vote_removed) {
        if (voteType === 1) {
          upvoteButton.classList.add(cssClasses.voted);
        } else {
          downvoteButton.classList.add(cssClasses.voted);
        }
      }
    }
  })
  .catch(error => {
    console.error('Error processing comment vote:', error);
  })
  .finally(() => {
    // Re-enable buttons
    upvoteButton.disabled = false;
    downvoteButton.disabled = false;
  });
};

/**
 * Highlight existing votes for the current user (if any)
 */
const highlightExistingVotes = () => {
  if (!config.isLoggedIn) {
    return;
  }
  
  // Check for user_vote data in the data attributes
  document.querySelectorAll(selectors.comment).forEach(comment => {
    const userVote = parseInt(comment.dataset.userVote, 10);
    
    if (userVote === 1) {
      const upvoteButton = comment.querySelector(selectors.upvoteButton);
      if (upvoteButton) {
        upvoteButton.classList.add(cssClasses.voted);
      }
    } else if (userVote === -1) {
      const downvoteButton = comment.querySelector(selectors.downvoteButton);
      if (downvoteButton) {
        downvoteButton.classList.add(cssClasses.voted);
      }
    }
  });
};

/**
 * Initialize the comment rating functionality
 */
const init = () => {
  // Only initialize if comments exist on page
  if (!document.querySelector(selectors.comment)) {
    return;
  }
  
  // Add event listeners using event delegation
  document.addEventListener('click', e => {
    const upvoteTarget = e.target.closest(selectors.upvoteButton);
    const downvoteTarget = e.target.closest(selectors.downvoteButton);
    
    if (upvoteTarget) {
      handleUpvote(e);
    } else if (downvoteTarget) {
      handleDownvote(e);
    }
  });
  
  // Pre-highlight any existing votes (if user is logged in)
  highlightExistingVotes();
};

// Export functions that should be available to other modules
const initCommentsRating = {
  init,
  handleUpvote,
  handleDownvote,
  handleVote,
  highlightExistingVotes,
  selectors,
  cssClasses,
  config
};

export default initCommentsRating;
